import { CommandKind, LevelPhase, TimedCommand } from "@highmarch/domain";
import { LevelSession } from "./levelSession";
import { ResumeReplay } from "./suspend";

/**
 * Undo the last card before the waves start (DESIGN §4.8.2, decision D17). No sim change: the session is rebuilt from
 * its own inputs and its command log minus the last card, replayed to the tick it had reached, exactly as a suspended
 * battle is resumed (suspend.ts). Deterministic, so the result is the run that never played that card.
 */

/**
 * Where the log is cut to take back the last card played: at the PickCard that started it, so the card goes back to
 * its offer and anything done after it (a reroll of the next hand, a skip, a card picked but not placed) goes too.
 * A pick taken back by UnpickCard is not a card played. Null when no card has been played.
 */
export function undoCut(commands: readonly TimedCommand[]): number | null {
  for (let i = commands.length - 1; i >= 0; i--) {
    if (commands[i].command.kind !== CommandKind.PickCard) continue;
    const next = commands[i + 1]?.command.kind;
    // the pick in hand (nothing after it) or one taken back is not played; only a pick followed by its play counts
    if (next === undefined || next === CommandKind.UnpickCard) continue;
    return i;
  }
  return null;
}

/** Undo is offered from the opening draft until START WAVES, when there is a card to take back and none is in hand. */
export function canUndoCard(session: LevelSession): boolean {
  const sim = session.sim, phase = sim.phase;
  if (phase !== LevelPhase.OpeningDraft && phase !== LevelPhase.Ready) return false;
  if (sim.currentOffer?.hasPick) return false;
  return undoCut(session.commands) !== null;
}

/** A new session: this one's inputs and its log minus the last card, replayed to this one's tick. Null if nothing to undo. */
export function undoLastCard(session: LevelSession): LevelSession | null {
  if (!canUndoCard(session)) return null;
  const cut = undoCut(session.commands)!;
  // the run's modifiers too: an Iron, Endless, Trial or Daily Road run stays one after an UNDO
  const next = new LevelSession(session.balance, session.level, session.seed, session.ownedTowers, session.towerLevel, session.firstClear, session.gear, session.modifiers);
  new ResumeReplay(next, session.commands.slice(0, cut), session.sim.tick).runToEnd();
  return next;
}
